// Checkbox, switch and radio group (D-062). Their own module, apart from the select and combobox in
// choice.tsx, so a first screen with a checkbox doesn't load the list controls.
import { useEffect, useId, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import type { SelectOption } from './choice';

const step = (list: { disabled?: boolean }[], from: number, by: number) => {
  for (let i = from + by; i >= 0 && i < list.length; i += by) if (!list[i].disabled) return i;
  return from;
};

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible label. Leave out for a bare box (give it `aria-label`). */
  label?: ReactNode;
  hint?: ReactNode;
  indeterminate?: boolean;
  testId?: string;
}

/** A checkbox in Dugzi's look (a real checkbox underneath, so forms and screen readers just work). */
export function Checkbox({ label, hint, indeterminate, className = '', testId, ...rest }: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  const hid = useId();
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
  const box = <input ref={ref} type="checkbox" className={`dz-checkbox ${label ? '' : className}`} aria-describedby={hint ? hid : undefined} data-testid={testId ?? (rest as { 'data-testid'?: string })['data-testid']} {...rest} />;
  if (!label) return box;
  return (
    <label className={`dz-check ${className}`}>
      {box}
      <span className="dz-check__text"><span>{label}</span>{hint && <small id={hid}>{hint}</small>}</span>
    </label>
  );
}

export interface SwitchProps { checked: boolean; onChange(v: boolean): void; label: ReactNode; hint?: ReactNode; labelHidden?: boolean; disabled?: boolean; testId?: string; id?: string }

/** On/off that takes effect at once (WAI-ARIA switch). Use a Checkbox when it's part of a form you submit. */
export function Switch({ checked, onChange, label, hint, labelHidden, disabled, testId, id }: SwitchProps) {
  const auto = useId(); const sid = id ?? auto;
  const btn = (
    <button type="button" role="switch" id={sid} aria-checked={checked} aria-labelledby={labelHidden ? undefined : `${sid}-l`} aria-label={labelHidden && typeof label === 'string' ? label : undefined}
      aria-describedby={hint ? `${sid}-h` : undefined} className="dz-switch" onClick={() => onChange(!checked)} data-testid={testId} disabled={disabled}><span aria-hidden /></button>
  );
  if (labelHidden) return btn;
  return (
    <div className="dz-switch-row">
      {btn}
      <span className="dz-switch-row__text"><label id={`${sid}-l`} htmlFor={sid}>{label}</label>{hint && <small id={`${sid}-h`}>{hint}</small>}</span>
    </div>
  );
}

export interface RadioGroupProps<T extends string | number> {
  label: ReactNode; value: T; onChange(v: T): void; options: SelectOption<T>[];
  labelHidden?: boolean; variant?: 'list' | 'segmented'; orientation?: 'vertical' | 'horizontal'; testId?: string;
}

/** One of a few (WAI-ARIA radio group): Tab enters at the chosen one, arrows move and choose. */
export function RadioGroup<T extends string | number>({ label, value, onChange, options, labelHidden, variant = 'list', orientation, testId }: RadioGroupProps<T>) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const sel = options.findIndex((o) => o.value === value);
  const focusable = sel >= 0 ? sel : step(options, -1, 1);
  const go = (i: number) => { const o = options[i]; if (!o || o.disabled) return; onChange(o.value); ref.current?.querySelectorAll<HTMLElement>('[role="radio"]')[i]?.focus(); };
  const dir = orientation ?? (variant === 'segmented' ? 'horizontal' : 'vertical');
  return (
    <div className={`dz-radio-field${variant === 'segmented' ? ' dz-radio-field--seg' : ''}`}>
      {!labelHidden && <span className="dz-field__label" id={`${id}-l`}>{label}</span>}
      <div ref={ref} role="radiogroup" tabIndex={-1} aria-labelledby={labelHidden ? undefined : `${id}-l`} aria-label={labelHidden && typeof label === 'string' ? label : undefined} aria-orientation={dir}
        className={variant === 'segmented' ? 'dz-segmented' : `dz-radios${dir === 'horizontal' ? ' dz-radios--row' : ''}`} data-testid={testId}
        onKeyDown={(e) => {
          const fwd = e.key === 'ArrowDown' || e.key === 'ArrowRight', back = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
          if (!fwd && !back) return;
          e.preventDefault();
          const from = sel >= 0 ? sel : 0;
          let i = from;
          for (let k = 1; k <= options.length; k++) { const j = (from + (fwd ? k : -k) + options.length) % options.length; if (!options[j].disabled) { i = j; break; } }
          go(i);
        }}>
        {options.map((o, i) => (
          <button key={String(o.value)} type="button" role="radio" aria-checked={o.value === value} tabIndex={i === focusable ? 0 : -1} disabled={o.disabled}
            className={variant === 'segmented' ? undefined : 'dz-radio'} onClick={() => go(i)}>
            {variant === 'segmented' ? <>{o.icon}{o.label}</> : <><span className="dz-radio__dot" aria-hidden /><span className="dz-radio__text"><span>{o.label}</span>{o.hint && <small>{o.hint}</small>}</span></>}
          </button>
        ))}
      </div>
    </div>
  );
}
